import { useEffect, useRef, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { ApiError } from '../api/types';
import { theme } from '../theme';
import { fmtK, fmtMoney } from '../utils/format';
import { celebrateBig } from '../utils/confetti';
import { hashStr } from '../utils/coins';
import { BOSS_REWARD, monthKeyOf, seasonFinaleState, summarizeSeason } from '../utils/game';

const RU_MON_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const NUM: React.CSSProperties = {
  fontFamily: theme.fontDisplay,
  fontVariationSettings: "'opsz' 144, 'WONK' 0",
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Финал сезона — Season-poster с барабаном (Антверпен 2.0 §1):
 * тушь-афиша итогов + вертикальный барабан процентов. Механика розыгрыша
 * (детерминированный hash, билеты повышают потолок) — без изменений.
 */
export function SeasonFinale() {
  const { viewData, addGameEvent } = useBudget();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [drum, setDrum] = useState<number | null>(null);
  const spinTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (spinTimer.current) clearInterval(spinTimer.current);
  }, []);

  if (!viewData) return null;
  const now = new Date();
  const state = seasonFinaleState(viewData, now);
  if (!state) return null;

  const monthName = RU_MON_NOM[Number(state.month.slice(5, 7)) - 1];
  const monthNum = state.month.slice(5, 7);
  const sum = summarizeSeason(viewData, state.month);

  const cap = Math.min(16, sum.tickets);
  const hasLottery = sum.saved > 0 && sum.tickets > 0;
  // детерминированный розыгрыш — повторное нажатие и второе устройство дают то же
  const pctFinal = hasLottery ? 5 + (hashStr(`${state.month}:${sum.tickets}:${sum.saved}`) % cap) : 0;
  const amountFinal = hasLottery
    ? Math.max(100, Math.round((sum.saved * pctFinal) / 100 / 100) * 100)
    : 0;

  async function commitClose() {
    if (!state) return;
    setBusy(true);
    try {
      const bossBonus = sum.bossWin ? BOSS_REWARD : 0;
      const r = await addGameEvent({
        id: `season_${state.month}`,
        type: 'season_close',
        month: monthKeyOf(now), // бонус падает в новый сезон
        week: state.month,
        coins: bossBonus,
        payload: JSON.stringify({
          lottery_amount: amountFinal,
          pct: pctFinal,
          tickets: sum.tickets,
          boss: sum.bossWin,
          boss_cat: sum.bossCat,
          saved: sum.saved,
        }),
      });
      if (r.existing) {
        // сезон уже закрыт (второе устройство) — плашка покажет серверный payload
        toast.show('Сезон уже был закрыт — результат ниже');
        return;
      }
      celebrateBig();
      if (amountFinal > 0) {
        toast.reward({
          title: `Выпало ${pctFinal}%`,
          crit: true,
          amount: fmtMoney(amountFinal),
          coin: false,
          sub: `разрешённая покупка из сэкономленного — без вины${r.synced ? '' : ' · синхронизируется позже'}`,
        });
      } else {
        toast.show(`Сезон закрыт. Новый месяц — чистый лист${r.synced ? '' : ' · синх позже'}`);
      }
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
      setDrum(null);
    }
  }

  function spin() {
    if (busy) return;
    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!hasLottery || reduced) {
      void commitClose();
      return;
    }
    setBusy(true);
    let step = 0;
    spinTimer.current = setInterval(() => {
      step += 1;
      if (step >= 15) {
        if (spinTimer.current) clearInterval(spinTimer.current);
        setDrum(pctFinal);
        setTimeout(() => void commitClose(), 350);
        return;
      }
      setDrum(5 + ((step * 7) % cap)); // листаем значения барабана
    }, 110);
  }

  // --- закрытый сезон: результат-плашка из серверного payload ---
  if (state.closed) {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(state.closed.payload || '{}') as Record<string, unknown>;
    } catch {
      /* битый payload — покажем без деталей */
    }
    const amount = Number(payload.lottery_amount ?? 0);
    const pct = Number(payload.pct ?? 0);
    return (
      <div
        className="rise"
        style={{ background: theme.colors.ink, color: theme.colors.paper, borderRadius: theme.radius.sm, padding: '14px 16px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span
            style={{ fontFamily: theme.fontDisplay, fontWeight: 420, fontVariationSettings: "'opsz' 144, 'WONK' 1", fontSize: 19 }}
          >
            {amount > 0 ? `Выпало ${pct}%` : `Сезон «${monthName}» закрыт`}
          </span>
          {amount > 0 && (
            <span style={{ ...NUM, fontSize: 22, color: theme.colors.greenSoft }}>{fmtMoney(amount)}</span>
          )}
        </div>
        <div
          style={{
            borderTop: '1px solid rgba(236,233,223,.25)',
            marginTop: 8,
            paddingTop: 7,
            fontSize: 12,
            color: theme.colors.paperSub,
            fontStyle: 'italic',
          }}
        >
          {amount > 0 ? 'разрешённая покупка из сэкономленного — потрать без вины · ' : ''}
          {payload.boss === true && <>босс повержен (+{BOSS_REWARD}) · </>}
          {payload.boss === false && <>босс устоял · </>}
          сезон «{monthName}» закрыт
        </div>
      </div>
    );
  }

  // --- афиша с барабаном ---
  return (
    <div
      className="rise"
      style={{ background: theme.colors.ink, color: theme.colors.paper, borderRadius: theme.radius.sm, padding: '16px 18px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="caption" style={{ fontSize: 11, color: theme.colors.paperSub }}>
          финал сезона · {monthNum}
        </span>
        <span style={{ fontSize: 12, color: theme.colors.paperSub, fontVariantNumeric: 'tabular-nums' }}>
          {sum.tickets} {sum.tickets === 1 ? 'билет' : sum.tickets < 5 ? 'билета' : 'билетов'}
        </span>
      </div>
      <div style={{ fontFamily: theme.fontDisplay, fontWeight: 420, fontVariationSettings: "'opsz' 144, 'WONK' 1", fontSize: 28, marginTop: 6 }}>
        {monthName}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          marginTop: 12,
          borderTop: '1px solid rgba(236,233,223,.25)',
          paddingTop: 10,
        }}
      >
        <div>
          <div className="caption" style={{ fontSize: 10, letterSpacing: '.14em', color: theme.colors.paperSub }}>сохранено</div>
          <div style={{ ...NUM, fontSize: 18, marginTop: 2, color: sum.saved >= 0 ? theme.colors.greenSoft : theme.colors.redSoft }}>
            {fmtK(sum.saved)}
          </div>
        </div>
        <div>
          <div className="caption" style={{ fontSize: 10, letterSpacing: '.14em', color: theme.colors.paperSub }}>монеты</div>
          <div style={{ ...NUM, fontSize: 18, marginTop: 2 }}>{sum.coins}</div>
        </div>
        {sum.bossCat && (
          <div style={{ textAlign: 'right' }}>
            <div className="caption" style={{ fontSize: 10, letterSpacing: '.14em', color: theme.colors.paperSub }}>босс</div>
            <div style={{ ...NUM, fontSize: 18, marginTop: 2, color: sum.bossWin ? theme.colors.greenSoft : theme.colors.paperSub }}>
              {sum.bossWin ? 'выстоял' : 'пробил'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14 }}>
        {hasLottery && (
          <div
            style={{
              width: 58,
              height: 78,
              border: `1.5px solid ${theme.colors.paper}`,
              borderRadius: theme.radius.sm,
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: 26,
                marginTop: -13,
                borderTop: '1px solid rgba(236,233,223,.5)',
                borderBottom: '1px solid rgba(236,233,223,.5)',
              }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
                ...NUM,
              }}
            >
              <span style={{ fontSize: 12, opacity: 0.3 }}>{drum === null ? '·' : `${Math.max(5, drum - 1)}%`}</span>
              <span style={{ fontSize: 19 }}>{drum === null ? '?' : `${drum}%`}</span>
              <span style={{ fontSize: 12, opacity: 0.3 }}>{drum === null ? '·' : `${Math.min(5 + cap - 1, drum + 1)}%`}</span>
            </div>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.4, color: theme.colors.paperSub, fontStyle: 'italic' }}>
            {hasLottery
              ? 'билеты повышают потолок процента — выигрыш станет разрешённой покупкой'
              : 'итоги месяца зафиксируются, новый сезон начнётся с чистого листа'}
          </div>
          <button
            onClick={spin}
            disabled={busy}
            style={{
              marginTop: 8,
              padding: '10px 16px',
              background: theme.colors.paper,
              color: theme.colors.ink,
              border: 'none',
              borderRadius: theme.radius.sm,
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {busy ? '…' : hasLottery ? 'Крутить барабан' : 'Закрыть сезон'}
          </button>
        </div>
      </div>
    </div>
  );
}
